import React, { useMemo, useState } from "react";
import Swal from "sweetalert2";
import { useProjectContext } from "../layout/ProjectContext";
import {
  useGetProjectQuery,
  useUpdateProjectMutation,
} from "../features/projects/apiSlice";
import {
  useGetSiteLayoutQuery,
  type SiteLayoutDevicePosition,
} from "../features/management-dashboard/apiSlice";
import {
  useGetDevicesByProjectQuery,
  useUpdateDeviceMutation,
  useDeleteDeviceMutation,
} from "../features/devices/apiSlice";
import type { MonitoringDevice } from "../types/domain";
import EmptyHint from "../components/EmptyHint";
import SiteLayoutManager from "../features/site-layout/SiteLayoutManager";
import {
  useGetAlertProfilesQuery,
  useCreateAlertProfileMutation,
  useUpdateAlertProfileMutation,
  type AlertLevel,
} from "../features/alert-profiles/apiSlice";
import { isMovementType } from "../utils/movementTypes";
import {
  DEFAULT_VIBRATION_PROFILE_DESCRIPTION,
  sortVibrationProfiles,
} from "../utils/vibrationProfiles";
import dayjs from "dayjs";

const TYPE_OPTIONS: { key: string; label: string }[] = [
  { key: "ground_movement", label: "Ground Movement" },
  { key: "building_movement", label: "Building Movement" },
  { key: "utility_movement", label: "Utility Movement" },
  { key: "ground_water", label: "Ground Water" },
  { key: "tilting", label: "Tilting" },
  { key: "vibration", label: "Vibration" },
  { key: "crack", label: "Crack Gauge" },
];

interface DeviceForm {
  device_name: string;
  device_type: string;
  real_easting: string;
  real_northing: string;
  installation_date: string;
  pre_existing_value: string;
  pre_existing_value2: string;
  pre_existing_value3: string;
  // Ground water alert levels (mPD)
  gw_lowest: string;
  gw_above_lowest: string;
  gw_below_highest: string;
  gw_highest: string;
  alert_profile_id: string;
  // Movement configuration (immutable after create)
  movement_mode: "1" | "2" | "3";
  // Tilting configuration
  tilt_entry_mode: "slope_ratio" | "movement";
  tilt_height: string;
  tilt_pre_existing_movement: string;
}

const EMPTY_DEVICE_FORM: DeviceForm = {
  device_name: "",
  device_type: "ground_movement",
  real_easting: "",
  real_northing: "",
  installation_date: "",
  pre_existing_value: "",
  pre_existing_value2: "",
  pre_existing_value3: "",
  gw_lowest: "",
  gw_above_lowest: "",
  gw_below_highest: "",
  gw_highest: "",
  alert_profile_id: "",
  movement_mode: "1",
  tilt_entry_mode: "slope_ratio",
  tilt_height: "",
  tilt_pre_existing_movement: "0",
};

const TYPE_LABELS: Record<string, string> = {
  ground_settlement: "Ground Settlement",
  building_settlement: "Building Settlement",
  utility_settlement: "Utility Settlement",
  ground_water: "Ground Water",
  tilting: "Tilting",
  vibration: "Vibration",
  crack: "Crack Gauge",
  movement: "Movement",
  ground_movement: "Ground Movement",
  building_movement: "Building Movement",
  utility_movement: "Utility Movement",
};

const TYPE_COLORS: Record<string, string> = {
  ground_settlement: "rgba(13, 110, 253, 0.08)",
  building_settlement: "rgba(255, 193, 7, 0.12)",
  utility_settlement: "rgba(255, 152, 0, 0.12)",
  ground_water: "rgba(68, 170, 255, 0.08)",
  tilting: "rgba(220, 53, 69, 0.08)",
  vibration: "rgba(136, 136, 136, 0.08)",
  crack: "rgba(255, 68, 170, 0.08)",
  movement: "rgba(16, 185, 129, 0.10)",
  ground_movement: "rgba(16, 185, 129, 0.10)",
  building_movement: "rgba(5, 150, 105, 0.10)",
  utility_movement: "rgba(52, 211, 153, 0.12)",
};

const ProjectSetup: React.FC = () => {
  const { projectId } = useProjectContext();
  const { data: currentProject } = useGetProjectQuery(projectId as number, {
    skip: projectId == null,
  });

  const { data: siteLayout, isFetching } = useGetSiteLayoutQuery(
    projectId as number,
    { skip: projectId == null },
  );
  const { data: fullDevices } = useGetDevicesByProjectQuery(
    projectId as number,
    { skip: projectId == null },
  );
  const [updateDevice, { isLoading: updating }] = useUpdateDeviceMutation();
  const [deleteDevice] = useDeleteDeviceMutation();
  const [updateProject, { isLoading: savingProject }] =
    useUpdateProjectMutation();
  const { data: alertProfiles } = useGetAlertProfilesQuery(
    projectId ?? undefined,
    { skip: projectId == null },
  );
  const [createAlertProfile] = useCreateAlertProfileMutation();
  const [updateAlertProfile] = useUpdateAlertProfileMutation();

  const [modalOpen, setModalOpen] = useState(false);
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const [projectForm, setProjectForm] = useState({
    name: "",
    project_code: "",
    contract_no: "",
    contractor_name: "",
    description: "",
  });
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<DeviceForm>(EMPTY_DEVICE_FORM);
  const [gwProfileId, setGwProfileId] = useState<number | null>(null);
  const [editReason, setEditReason] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("All types");
  const [search, setSearch] = useState<string>("");

  const deviceById = useMemo(() => {
    const m = new Map<string, MonitoringDevice>();
    (fullDevices ?? []).forEach((d) => m.set(d.id, d));
    return m;
  }, [fullDevices]);

  const vibrationProfiles = useMemo(
    () => sortVibrationProfiles(alertProfiles),
    [alertProfiles],
  );

  const openEdit = (id: string) => {
    const d = deviceById.get(id);
    if (!d) {
      void Swal.fire({
        icon: "warning",
        title: "Not ready",
        text: "Device details are still loading. Please try again.",
      });
      return;
    }
    setEditId(id);

    // Populate GW alert level fields if applicable
    let gwLowest = "",
      gwAboveLowest = "",
      gwBelowHighest = "",
      gwHighest = "";
    let foundGwProfileId: number | null = null;
    if (d.device_type === "ground_water" && d.alert_profile_id) {
      const gwProfile = (alertProfiles ?? []).find(
        (p) => p.id === d.alert_profile_id,
      );
      if (gwProfile && Array.isArray(gwProfile.alert_level_json)) {
        const levels = gwProfile.alert_level_json as AlertLevel[];
        gwLowest = String(levels.find((l) => l.name === "Lowest")?.value ?? "");
        gwAboveLowest = String(
          levels.find((l) => l.name === "Level above the lowest")?.value ?? "",
        );
        gwBelowHighest = String(
          levels.find((l) => l.name === "Level below the highest")?.value ?? "",
        );
        gwHighest = String(
          levels.find((l) => l.name === "Highest")?.value ?? "",
        );
        foundGwProfileId = gwProfile.id;
      }
    }
    setGwProfileId(foundGwProfileId);

    const defaultVibrationProfile =
      vibrationProfiles.find(
        (p) => p.description === DEFAULT_VIBRATION_PROFILE_DESCRIPTION,
      ) ?? vibrationProfiles[0];

    setForm({
      device_name: d.device_name,
      device_type: d.device_type,
      real_easting: String(d.real_easting ?? ""),
      real_northing: String(d.real_northing ?? ""),
      installation_date: d.installation_date
        ? dayjs(d.installation_date ?? "").format("YYYY-MM-DD")
        : "",
      // dayjs(d.installation_date ?? "").format("YYYY-MM-DD") ?? "",
      pre_existing_value:
        [
          "ground_settlement",
          "building_settlement",
          "utility_settlement",
          "movement",
          "ground_movement",
          "building_movement",
          "utility_movement",
        ].includes(d.device_type) &&
        d.pre_existing_value != null &&
        d.pre_existing_value !== 0
          ? String(Number(d.pre_existing_value) * 1000)
          : String(d.pre_existing_value ?? ""),
      pre_existing_value2:
        d.device_type === "crack" || isMovementType(d.device_type)
          ? String(d.pre_existing_value2 ?? "")
          : "",
      pre_existing_value3: isMovementType(d.device_type)
        ? String(d.pre_existing_value3 ?? "")
        : "",
      gw_lowest: gwLowest,
      gw_above_lowest: gwAboveLowest,
      gw_below_highest: gwBelowHighest,
      gw_highest: gwHighest,
      alert_profile_id:
        d.device_type === "vibration"
          ? String(d.alert_profile_id ?? defaultVibrationProfile?.id ?? "")
          : "",
      movement_mode: String(d.movement_mode ?? 1) as "1" | "2" | "3",
      tilt_entry_mode:
        (d.tilt_entry_mode as "slope_ratio" | "movement") ?? "slope_ratio",
      tilt_height: d.tilt_height != null ? String(d.tilt_height) : "",
      tilt_pre_existing_movement:
        d.tilt_pre_existing_movement != null
          ? String(d.tilt_pre_existing_movement)
          : "0",
    });
    setEditReason("");
    setModalOpen(true);
  };

  const canSave =
    form.device_name.trim() !== "" &&
    form.real_easting !== "" &&
    form.real_northing !== "" &&
    (form.device_type !== "tilting" ||
      form.tilt_entry_mode !== "movement" ||
      form.tilt_height !== "") &&
    (form.device_type !== "vibration" || form.alert_profile_id !== "") &&
    editReason.trim() !== "";

  const handleSave = async () => {
    if (editId == null) {
      throw new Error("Edit checkpoint modal requires an editId.");
    }
    if (!canSave || projectId == null) return;
    const currentEditId = editId;
    const base: Partial<MonitoringDevice> = {
      device_name: form.device_name.trim(),
      device_type: form.device_type,
      real_easting: Number(form.real_easting),
      real_northing: Number(form.real_northing),
      monitoring_frequency: "daily",
      installation_date: form.installation_date || undefined,
    };
    if (isMovementType(form.device_type)) {
      if (form.pre_existing_value !== "") {
        (base as any).pre_existing_value =
          Number(form.pre_existing_value) / 1000;
      }
      if (form.pre_existing_value2 !== "") {
        (base as any).pre_existing_value2 = Number(form.pre_existing_value2);
      }
      if (form.pre_existing_value3 !== "") {
        (base as any).pre_existing_value3 = Number(form.pre_existing_value3);
      }
      (base as any).movement_mode = Number(form.movement_mode);
      // Action profiles are assigned on the Action Profiles page, not here.
    } else if (form.device_type === "tilting") {
      (base as any).tilt_entry_mode = form.tilt_entry_mode;
      if (form.tilt_entry_mode === "movement") {
        (base as any).tilt_height =
          form.tilt_height !== "" ? Number(form.tilt_height) : null;
        (base as any).tilt_pre_existing_movement =
          form.tilt_pre_existing_movement !== ""
            ? Number(form.tilt_pre_existing_movement)
            : 0;
        // Pre-existing tilt is captured as a movement; keep the N-based field 0.
        (base as any).pre_existing_value = 0;
      } else if (form.pre_existing_value !== "") {
        (base as any).pre_existing_value = Number(form.pre_existing_value);
      }
    } else if (form.device_type === "vibration") {
      (base as any).alert_profile_id = Number(form.alert_profile_id);
    } else if (
      form.device_type !== "vibration" &&
      form.pre_existing_value !== ""
    ) {
      const rawValue = Number(form.pre_existing_value);
      (base as any).pre_existing_value = [
        "ground_settlement",
        "building_settlement",
        "utility_settlement",
      ].includes(form.device_type)
        ? rawValue / 1000
        : rawValue;
    }
    if (form.device_type === "crack" && form.pre_existing_value2 !== "") {
      (base as any).pre_existing_value2 = Number(form.pre_existing_value2);
    }
    try {
      if (form.device_type === "ground_water") {
        const lowest = Number(form.gw_lowest);
        const aboveLowest = Number(form.gw_above_lowest);
        const belowHighest = Number(form.gw_below_highest);
        const highest = Number(form.gw_highest);

        if (
          !Number.isFinite(lowest) ||
          !Number.isFinite(aboveLowest) ||
          !Number.isFinite(belowHighest) ||
          !Number.isFinite(highest)
        ) {
          void Swal.fire({
            icon: "warning",
            title: "Invalid ground water levels",
            text: "Ground water alert levels must be valid numbers.",
          });
          return;
        }

        if (highest <= lowest) {
          void Swal.fire({
            icon: "warning",
            title: "Invalid ground water levels",
            text: "Highest must be greater than Lowest.",
          });
          return;
        }

        if (highest - belowHighest <= lowest + aboveLowest) {
          void Swal.fire({
            icon: "warning",
            title: "Invalid ground water levels",
            text: "(Highest - Level below the highest) must be greater than (Lowest + Level above the lowest).",
          });
          return;
        }

        const gwLevels: AlertLevel[] = [
          {
            order: 1,
            name: "Lowest",
            value: lowest,
            entry_mode: "value",
          },
          {
            order: 2,
            name: "Level above the lowest",
            value: aboveLowest,
            entry_mode: "value",
          },
          {
            order: 3,
            name: "Level below the highest",
            value: belowHighest,
            entry_mode: "value",
          },
          {
            order: 4,
            name: "Highest",
            value: highest,
            entry_mode: "value",
          },
        ];
        const profileBody = {
          project_id: projectId,
          description: `GW: ${form.device_name.trim()}`,
          profile_type: "ground_water" as const,
          alert_level_json: gwLevels,
        };

        if (gwProfileId != null) {
          // Update existing profile + update device
          await updateAlertProfile({
            id: gwProfileId,
            body: profileBody,
          }).unwrap();
          await updateDevice({
            deviceId: currentEditId,
            body: { ...base, operation_reason: editReason.trim() },
          }).unwrap();
        } else {
          // No profile yet: create one, then link it to the device
          const profile = await createAlertProfile(profileBody).unwrap();
          await updateDevice({
            deviceId: currentEditId,
            body: {
              ...base,
              alert_profile_id: profile.id,
              operation_reason: editReason.trim(),
            },
          }).unwrap();
        }
      } else {
        await updateDevice({
          deviceId: currentEditId,
          body: { ...base, operation_reason: editReason.trim() },
        }).unwrap();
      }
      setModalOpen(false);
      void Swal.fire({
        icon: "success",
        title: "Checkpoint updated",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 2500,
        timerProgressBar: true,
      });
    } catch {
      void Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to save device.",
      });
    }
  };

  const handleDelete = async (id: string, name: string) => {
    const { value: reason, isConfirmed } = await Swal.fire({
      title: `Delete "${name}"`,
      input: "text",
      inputLabel: "Reason for deletion",
      inputPlaceholder: "Enter a reason…",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#dc3545",
      inputValidator: (v) => (!v.trim() ? "A reason is required." : null),
      didOpen: () => {
        const btn = Swal.getConfirmButton();
        if (btn) btn.disabled = true;
        const inp = Swal.getInput();
        if (inp) {
          inp.addEventListener("input", () => {
            if (btn) btn.disabled = !inp.value.trim();
          });
        }
      },
    });
    if (!isConfirmed || !reason || !reason.trim()) return;
    try {
      await deleteDevice({
        deviceId: id,
        operation_reason: reason.trim(),
      }).unwrap();
      void Swal.fire({
        icon: "success",
        title: `"${name}" deleted`,
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 2500,
        timerProgressBar: true,
      });
    } catch {
      void Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to delete device.",
      });
    }
  };

  const openProjectEdit = () => {
    if (currentProject) {
      setProjectForm({
        name: currentProject.name || "",
        project_code: currentProject.project_code || "",
        contract_no: currentProject.contract_no || "",
        contractor_name: currentProject.contractor_name || "",
        description: currentProject.description || "",
      });
      setProjectModalOpen(true);
    }
  };

  const handleSaveProject = async () => {
    if (!projectId) return;
    try {
      const codeRegex = /^[A-Z0-9]{6}$/;
      if (!codeRegex.test(projectForm.project_code.trim())) {
        void Swal.fire({
          icon: "warning",
          title: "Invalid project code",
          text: "Project code must be exactly 6 uppercase letters or digits (A–Z, 0–9).",
        });
        return;
      }
      await updateProject({
        id: projectId,
        body: {
          name: projectForm.name.trim(),
          project_code: projectForm.project_code.trim(),
          contract_no: projectForm.contract_no.trim() || undefined,
          contractor_name: projectForm.contractor_name.trim() || undefined,
          description: projectForm.description.trim() || undefined,
        },
      }).unwrap();
      setProjectModalOpen(false);
    } catch (err: any) {
      const msg =
        err?.data?.message ||
        (typeof err?.data === "string" ? err.data : null) ||
        "Failed to update project.";
      void Swal.fire({
        icon: "error",
        title: "Error",
        text: msg,
      });
    }
  };

  const devices: SiteLayoutDevicePosition[] = useMemo(
    () => siteLayout?.devices ?? [],
    [siteLayout],
  );

  const filteredDevices = useMemo(
    () =>
      devices.filter((d) => {
        if (
          typeFilter !== "All types" &&
          (TYPE_LABELS[d.type] || d.type) !== typeFilter
        )
          return false;
        if (search.trim()) {
          const q = search.toLowerCase();
          if (!d.name.toLowerCase().includes(q)) return false;
        }
        return true;
      }),
    [devices, typeFilter, search],
  );

  if (projectId == null) {
    return (
      <EmptyHint
        icon="bi-folder2-open"
        title="Select a project"
        subtitle="Pick a project from the top bar to set up checkpoints and layout."
      />
    );
  }

  return (
    <div
      style={{
        height: "calc(100vh - 120px)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <div className="page-head">
        <div>
          <h1>
            {currentProject?.name ??
              siteLayout?.project?.name ??
              `Project #${projectId}`}
          </h1>
          <div className="sub">
            {devices.length} checkpoint{devices.length === 1 ? "" : "s"}
            {currentProject?.contract_no && (
              <> · Contract: {currentProject.contract_no}</>
            )}
            {currentProject?.contractor_name && (
              <> · Contractor: {currentProject.contractor_name}</>
            )}
          </div>
          {currentProject?.description && (
            <div className="sub text-muted" style={{ marginTop: 4 }}>
              {currentProject.description}
            </div>
          )}
        </div>
        <button className="btn" onClick={openProjectEdit}>
          <i className="bi bi-pencil" /> Edit project
        </button>
      </div>

      <div
        style={{ display: "flex", gap: "1rem", flex: 1, overflow: "hidden" }}
      >
        <div
          className="card"
          style={{ flex: "0 0 80%", display: "flex", flexDirection: "column" }}
        >
          <div className="card-head">
            <h2>Site Layout</h2>
          </div>
          <div style={{ flex: 1, overflow: "hidden" }}>
            <SiteLayoutManager mode="edit" projectId={projectId} />
          </div>
        </div>

        {/* Checkpoints */}
        <div
          className="card"
          style={{ flex: "0 0 20%", display: "flex", flexDirection: "column" }}
        >
          <div className="card-head">
            <h2>Checkpoints</h2>
          </div>
          <div style={{ padding: "0 1rem 1rem" }}>
            <input
              className="input mb-2"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              className="select mb-3"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option>All types</option>
              {Array.from(
                new Set(devices.map((d) => TYPE_LABELS[d.type] || d.type)),
              ).map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
            {isFetching ? (
              <EmptyHint
                icon="bi-hourglass-split"
                title="Loading…"
                subtitle="Fetching checkpoints from API."
              />
            ) : filteredDevices.length === 0 ? (
              <EmptyHint
                icon="bi-cpu"
                title="No checkpoints"
                subtitle="No checkpoints match the current filters."
              />
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th style={{ textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDevices.map((d) => {
                    const full = deviceById.get(d.id);
                    const damaged = full?.is_damaged === 1;
                    const tempInaccessible =
                      full?.is_unable_measure_temperorily === 1;
                    const bgColor =
                      TYPE_COLORS[d.type] || "rgba(13, 110, 253, 0.08)";
                    return (
                      <tr key={d.id} style={{ backgroundColor: bgColor }}>
                        <td className="fw-600">
                          {d.name}
                          {damaged && (
                            <span
                              className="badge alarm"
                              style={{ marginLeft: 6 }}
                            >
                              Damaged
                            </span>
                          )}
                          {tempInaccessible && (
                            <span
                              className="badge warn"
                              style={{ marginLeft: 6 }}
                              title={
                                full?.unable_measure_temperorily_reason || ""
                              }
                            >
                              Inaccessible
                            </span>
                          )}
                        </td>
                        <td>{TYPE_LABELS[d.type] || d.type}</td>
                        <td
                          style={{ textAlign: "right", whiteSpace: "nowrap" }}
                        >
                          <button
                            className="btn btn-sm btn-ghost"
                            title="Edit"
                            onClick={() => openEdit(d.id)}
                          >
                            <i className="bi bi-pencil" />
                          </button>
                          <button
                            className="btn btn-sm btn-ghost"
                            title="Delete"
                            onClick={() => handleDelete(d.id, d.name)}
                          >
                            <i className="bi bi-trash" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Edit checkpoint modal
          NOTE: The "Add checkpoint" / placement UI lives in
          #file:DevicePlacementModal.tsx (new-front/src/features/site-layout/components/DevicePlacementModal.tsx).
          If you modify the add-checkpoint UI or fields here, make the same
          corresponding changes in that file so both create and edit stay in sync.
        */}
      <div className={`modal-backdrop ${modalOpen ? "show" : ""}`}>
        <div className="modal">
          <div className="modal-head">
            <h3>Edit checkpoint</h3>
            <button
              className="btn btn-ghost btn-icon"
              onClick={() => setModalOpen(false)}
            >
              <i className="bi bi-x-lg" />
            </button>
          </div>
          <div className="modal-body">
            <div className="grid cols-2">
              <div className="field">
                <label>Name</label>
                <input
                  className="input"
                  placeholder="e.g. GS-15"
                  value={form.device_name}
                  onChange={(e) =>
                    setForm({ ...form, device_name: e.target.value })
                  }
                />
              </div>
              <div className="field">
                <label>Type</label>
                <select
                  className="select"
                  value={form.device_type}
                  onChange={(e) =>
                    setForm({ ...form, device_type: e.target.value })
                  }
                  disabled
                >
                  {TYPE_OPTIONS.map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Northing (m)</label>
                <input
                  className="input"
                  type="number"
                  placeholder="819360.00"
                  value={form.real_northing}
                  onChange={(e) =>
                    setForm({ ...form, real_northing: e.target.value })
                  }
                />
              </div>
              <div className="field">
                <label>Easting (m)</label>
                <input
                  className="input"
                  type="number"
                  placeholder="832200.00"
                  value={form.real_easting}
                  onChange={(e) =>
                    setForm({ ...form, real_easting: e.target.value })
                  }
                />
              </div>

              <div className="field">
                <label>Installation date (optional)</label>
                <input
                  className="input"
                  type="date"
                  value={form.installation_date}
                  onChange={(e) =>
                    setForm({ ...form, installation_date: e.target.value })
                  }
                />
              </div>
              {form.device_type === "vibration" && (
                <div className="field">
                  <label>Action profile</label>
                  <select
                    className="select"
                    value={form.alert_profile_id}
                    onChange={(e) =>
                      setForm({ ...form, alert_profile_id: e.target.value })
                    }
                  >
                    {vibrationProfiles.length === 0 ? (
                      <option value="">No vibration profiles available</option>
                    ) : (
                      vibrationProfiles.map((profile) => (
                        <option key={profile.id} value={profile.id}>
                          {profile.description}
                        </option>
                      ))
                    )}
                  </select>
                </div>
              )}
              {form.device_type === "tilting" && (
                <div className="field">
                  <label>Entry mode</label>
                  <select
                    className="select"
                    value={form.tilt_entry_mode}
                    disabled
                    onChange={(e) =>
                      setForm({
                        ...form,
                        tilt_entry_mode: e.target.value as
                          | "slope_ratio"
                          | "movement",
                      })
                    }
                  >
                    <option value="slope_ratio">tiltmeter mode</option>
                    <option value="movement">Plumb-wire Mode</option>
                  </select>
                </div>
              )}
              {form.device_type === "tilting" &&
                form.tilt_entry_mode === "movement" && (
                  <>
                    <div className="field">
                      <label>
                        Height
                        <span
                          style={{
                            fontWeight: 400,
                            marginLeft: 4,
                            color: "var(--text-muted, #888)",
                          }}
                        >
                          (m)
                        </span>
                      </label>
                      <input
                        className="input"
                        type="number"
                        step="any"
                        placeholder="e.g. 3"
                        value={form.tilt_height}
                        onChange={(e) =>
                          setForm({ ...form, tilt_height: e.target.value })
                        }
                      />
                    </div>
                    <div className="field">
                      <label>
                        Pre-existing movement
                        <span
                          style={{
                            fontWeight: 400,
                            marginLeft: 4,
                            color: "var(--text-muted, #888)",
                          }}
                        >
                          (mm)
                        </span>
                      </label>
                      <input
                        className="input"
                        type="number"
                        step="any"
                        value={form.tilt_pre_existing_movement}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            tilt_pre_existing_movement: e.target.value,
                          })
                        }
                      />
                      {Number(form.tilt_height) > 0 &&
                        Number(form.tilt_pre_existing_movement) !== 0 && (
                          <span
                            style={{
                              fontSize: "0.78em",
                              color: "var(--text-muted, #888)",
                              marginTop: 3,
                              display: "block",
                            }}
                          >
                            Slope ratio 1 :{" "}
                            {Math.round(
                              (Number(form.tilt_height) * 1000) /
                                Math.abs(
                                  Number(form.tilt_pre_existing_movement),
                                ),
                            )}
                          </span>
                        )}
                    </div>
                  </>
                )}
              {form.device_type !== "vibration" &&
                !isMovementType(form.device_type) &&
                !(
                  form.device_type === "tilting" &&
                  form.tilt_entry_mode === "movement"
                ) && (
                  <div className="field">
                    <label>
                      Pre-existing value
                      {[
                        "ground_settlement",
                        "building_settlement",
                        "utility_settlement",
                      ].includes(form.device_type) && (
                        <span
                          style={{
                            fontWeight: 400,
                            marginLeft: 4,
                            color: "var(--text-muted, #888)",
                          }}
                        >
                          (mm)
                        </span>
                      )}
                      {["tilting"].includes(form.device_type) && (
                        <span
                          style={{
                            fontWeight: 400,
                            marginLeft: 4,
                            color: "var(--text-muted, #888)",
                          }}
                        >
                          (1:N — enter N)
                        </span>
                      )}
                      {["ground_water"].includes(form.device_type) && (
                        <span
                          style={{
                            fontWeight: 400,
                            marginLeft: 4,
                            color: "var(--text-muted, #888)",
                          }}
                        >
                          (m)
                        </span>
                      )}
                      {["crack"].includes(form.device_type) && (
                        <span
                          style={{
                            fontWeight: 400,
                            marginLeft: 4,
                            color: "var(--text-muted, #888)",
                          }}
                        >
                          X (mm)
                        </span>
                      )}
                      {["vibration"].includes(form.device_type) && null}
                    </label>
                    <input
                      className="input"
                      type="number"
                      placeholder={
                        [
                          "ground_settlement",
                          "building_settlement",
                          "utility_settlement",
                        ].includes(form.device_type)
                          ? "e.g. 2"
                          : "12.840"
                      }
                      value={form.pre_existing_value}
                      onChange={(e) =>
                        setForm({ ...form, pre_existing_value: e.target.value })
                      }
                    />
                    {[
                      "ground_settlement",
                      "building_settlement",
                      "utility_settlement",
                    ].includes(form.device_type) && (
                      <span
                        style={{
                          fontSize: "0.78em",
                          color: "var(--text-muted, #888)",
                          marginTop: 3,
                          display: "block",
                        }}
                      >
                        −ve: Settlement &nbsp;/&nbsp; +ve: Heave
                      </span>
                    )}
                  </div>
                )}
              {isMovementType(form.device_type) && (
                <>
                  <div className="field">
                    <label>
                      Pre-existing settlement
                      <span
                        style={{
                          fontWeight: 400,
                          marginLeft: 4,
                          color: "var(--text-muted, #888)",
                        }}
                      >
                        (mm)
                      </span>
                    </label>
                    <input
                      className="input"
                      type="number"
                      placeholder="e.g. -2"
                      value={form.pre_existing_value}
                      onChange={(e) =>
                        setForm({ ...form, pre_existing_value: e.target.value })
                      }
                    />
                    <span
                      style={{
                        fontSize: "0.78em",
                        color: "var(--text-muted, #888)",
                        marginTop: 3,
                        display: "block",
                      }}
                    >
                      -ve: Settlement &nbsp;/&nbsp; +ve: Heave
                    </span>
                  </div>
                  <div className="field">
                    <label>
                      Pre-existing northing change
                      <span
                        style={{
                          fontWeight: 400,
                          marginLeft: 4,
                          color: "var(--text-muted, #888)",
                        }}
                      >
                        (mm)
                      </span>
                    </label>
                    <input
                      className="input"
                      type="number"
                      placeholder="e.g. 0"
                      value={form.pre_existing_value2}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          pre_existing_value2: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="field">
                    <label>
                      Pre-existing easting change
                      <span
                        style={{
                          fontWeight: 400,
                          marginLeft: 4,
                          color: "var(--text-muted, #888)",
                        }}
                      >
                        (mm)
                      </span>
                    </label>
                    <input
                      className="input"
                      type="number"
                      placeholder="e.g. 0"
                      value={form.pre_existing_value3}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          pre_existing_value3: e.target.value,
                        })
                      }
                    />
                  </div>
                </>
              )}
              {form.device_type === "crack" && (
                <div className="field">
                  <label>
                    Pre-existing value Y
                    <span
                      style={{
                        fontWeight: 400,
                        marginLeft: 4,
                        color: "var(--text-muted, #888)",
                      }}
                    >
                      (mm)
                    </span>
                  </label>
                  <input
                    className="input"
                    type="number"
                    placeholder="12.840"
                    value={form.pre_existing_value2}
                    onChange={(e) =>
                      setForm({ ...form, pre_existing_value2: e.target.value })
                    }
                  />
                </div>
              )}
              {form.device_type === "ground_water" && (
                <>
                  <div
                    className="field"
                    style={{ gridColumn: "span 2", marginTop: 8 }}
                  >
                    <label style={{ fontWeight: 600, fontSize: "0.9em" }}>
                      Ground Water Alert Levels (mPD)
                    </label>
                    <div
                      style={{
                        fontSize: "0.82em",
                        color: "var(--text-muted, #888)",
                        marginTop: 2,
                      }}
                    >
                      Lowest &amp; Highest are absolute values. The two middle
                      levels are deltas from those bounds.
                    </div>
                  </div>
                  <div className="field">
                    <label>Lowest (mPD)</label>
                    <input
                      className="input"
                      type="number"
                      step="any"
                      placeholder="e.g. 0.50"
                      value={form.gw_lowest}
                      onChange={(e) =>
                        setForm({ ...form, gw_lowest: e.target.value })
                      }
                    />
                  </div>
                  <div className="field">
                    <label>Level above the lowest (delta, m)</label>
                    <input
                      className="input"
                      type="number"
                      step="any"
                      placeholder="e.g. 0.20"
                      value={form.gw_above_lowest}
                      onChange={(e) =>
                        setForm({ ...form, gw_above_lowest: e.target.value })
                      }
                    />
                  </div>
                  <div className="field">
                    <label>Level below the highest (delta, m)</label>
                    <input
                      className="input"
                      type="number"
                      step="any"
                      placeholder="e.g. 0.20"
                      value={form.gw_below_highest}
                      onChange={(e) =>
                        setForm({ ...form, gw_below_highest: e.target.value })
                      }
                    />
                  </div>
                  <div className="field">
                    <label>Highest (mPD)</label>
                    <input
                      className="input"
                      type="number"
                      step="any"
                      placeholder="e.g. 3.00"
                      value={form.gw_highest}
                      onChange={(e) =>
                        setForm({ ...form, gw_highest: e.target.value })
                      }
                    />
                  </div>
                </>
              )}
              {isMovementType(form.device_type) && (
                <>
                  <div className="field">
                    <label>Scope of Measurement</label>
                    <select
                      className="select"
                      value={form.movement_mode}
                      disabled
                    >
                      <option value="2">Lateral movement only</option>
                      <option value="3">Lateral + vertical (settlement)</option>
                      <option value="1">Vertical (settlement) only</option>
                    </select>
                  </div>
                  {["2", "3"].includes(form.movement_mode) && (
                    <div className="field">
                      <label>Lateral input</label>
                      <input
                        className="input"
                        value="Northing + Easting"
                        disabled
                        readOnly
                      />
                    </div>
                  )}
                  <div className="field" style={{ gridColumn: "span 2" }}>
                    <span
                      style={{
                        fontSize: "0.82em",
                        color: "var(--text-muted, #888)",
                      }}
                    >
                      Action profiles are assigned on the Action Profiles page.
                      A movement checkpoint uses two profiles: Movement
                      (Settlement) and Movement (Lateral).
                    </span>
                  </div>
                </>
              )}
            </div>
            <div className="field" style={{ marginTop: 12 }}>
              <label>Reason for change (required)</label>
              <input
                className="input"
                placeholder="e.g. Corrected coordinates"
                value={editReason}
                onChange={(e) => setEditReason(e.target.value)}
              />
            </div>
          </div>
          <div className="modal-foot">
            <button className="btn" onClick={() => setModalOpen(false)}>
              Cancel
            </button>
            <button
              className={
                !canSave || updating ? "btn btn-ghost" : "btn btn-primary"
              }
              disabled={!canSave || updating}
              onClick={handleSave}
            >
              {updating ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </div>

      {/* Edit project modal */}
      <div className={`modal-backdrop ${projectModalOpen ? "show" : ""}`}>
        <div className="modal">
          <div className="modal-head">
            <h3>Edit project</h3>
            <button
              className="btn btn-ghost btn-icon"
              onClick={() => setProjectModalOpen(false)}
            >
              <i className="bi bi-x-lg" />
            </button>
          </div>
          <div className="modal-body">
            <div className="field mb-3">
              <label>Name</label>
              <input
                className="input"
                placeholder="Project name"
                value={projectForm.name}
                onChange={(e) =>
                  setProjectForm({ ...projectForm, name: e.target.value })
                }
              />
            </div>
            <div className="field mb-3">
              <label>
                Project Code <span style={{ color: "var(--c-danger)" }}>*</span>
              </label>
              <input
                className="input"
                placeholder="e.g. AB1234"
                maxLength={6}
                value={projectForm.project_code}
                onChange={(e) =>
                  setProjectForm({
                    ...projectForm,
                    project_code: e.target.value
                      .toUpperCase()
                      .replace(/[^A-Z0-9]/g, ""),
                  })
                }
              />
              <span className="text-muted text-sm">
                6 uppercase letters/digits, unique per project
              </span>
            </div>
            <div className="field mb-3">
              <label>Contract No.</label>
              <input
                className="input"
                placeholder="e.g. HK/2024/001"
                value={projectForm.contract_no}
                onChange={(e) =>
                  setProjectForm({
                    ...projectForm,
                    contract_no: e.target.value,
                  })
                }
              />
            </div>
            <div className="field mb-3">
              <label>Contractor Name</label>
              <input
                className="input"
                placeholder="e.g. ABC Construction Sdn Bhd"
                value={projectForm.contractor_name}
                onChange={(e) =>
                  setProjectForm({
                    ...projectForm,
                    contractor_name: e.target.value,
                  })
                }
              />
            </div>
            <div className="field">
              <label>Description</label>
              <textarea
                className="textarea"
                rows={4}
                placeholder="Project description (optional)"
                value={projectForm.description}
                onChange={(e) =>
                  setProjectForm({
                    ...projectForm,
                    description: e.target.value,
                  })
                }
              />
            </div>
          </div>
          <div className="modal-foot">
            <button className="btn" onClick={() => setProjectModalOpen(false)}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              disabled={
                !projectForm.name.trim() ||
                projectForm.project_code.trim().length !== 6 ||
                savingProject
              }
              onClick={handleSaveProject}
            >
              {savingProject ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectSetup;
